import { type FC, memo, useState } from 'react';

import type { ContractInsuranceEntityDetail } from '@/types/contract';
import { Pill, PillSwitch, PillSwitchOption, Typography } from '@/ui';

import './styles.css';
import type { Props } from './types';

const groupBy = (
  entities: ContractInsuranceEntityDetail[],
  key: (e: ContractInsuranceEntityDetail) => string
) => {
  const groups = new Map<string, ContractInsuranceEntityDetail[]>();
  entities.forEach((entity) =>
    groups.set(key(entity), [...(groups.get(key(entity)) ?? []), entity])
  );
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
};

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const ContractProfileEntities: FC<Props> = ({ entities, newEntityIds, version }) => {
  const types = groupBy(entities, (entity) => entity.insurance_type).sort(
    ([, a], [, b]) => b.length - a.length
  );
  const stateCount = new Set(entities.map((entity) => entity.jurisdiction_name)).size;
  const [selected, setSelected] = useState<string>();
  // Fall back to the largest type when the selection isn't in this version's entities.
  const [selectedType, typeEntities] = types.find(([type]) => type === selected) ??
    types[0] ?? ['', []];

  return (
    <div className="flex min-w-0 flex-col p-4 2xl:absolute 2xl:inset-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Typography variant="label" as="h4" muted>
          Entities in this profile
        </Typography>
        <Typography variant="caption" muted>
          {pluralize(types.length, 'insurance type', 'insurance types')} ·{' '}
          {pluralize(stateCount, 'state', 'states')}
        </Typography>
      </div>
      <PillSwitch
        value={selectedType}
        onChange={setSelected}
        size="sm"
        className="mt-3 max-w-full shrink-0 flex-wrap self-start rounded-2xl">
        {types.map(([type, entitiesOfType]) => (
          <PillSwitchOption key={type} value={type}>
            {type}
            <span className="opacity-70">{entitiesOfType.length}</span>
          </PillSwitchOption>
        ))}
      </PillSwitch>
      <ul className="mt-2 max-h-96 min-h-0 flex-1 divide-y divide-uniform-gray-100 overflow-y-auto 2xl:max-h-none dark:divide-uniform-gray-700">
        {groupBy(typeEntities, (entity) => entity.insurance_provider_name).map(
          ([provider, providerEntities]) => (
            <li key={provider} className="grid gap-2 py-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <div>
                <Typography weight="semibold">{provider}</Typography>
                <Typography variant="caption" muted>
                  {pluralize(providerEntities.length, 'state', 'states')}
                </Typography>
              </div>
              <div className="flex flex-wrap content-start gap-2">
                {[...providerEntities]
                  .sort((a, b) =>
                    a.jurisdiction_abbreviation.localeCompare(b.jurisdiction_abbreviation)
                  )
                  .map((entity) => (
                    <Pill
                      key={entity.id}
                      size="sm"
                      variant="outline"
                      color={newEntityIds.has(entity.id) ? 'secondary' : 'neutral'}>
                      <abbr title={entity.jurisdiction_name} className="no-underline">
                        {entity.jurisdiction_abbreviation}
                      </abbr>
                      {newEntityIds.has(entity.id) && (
                        <span className="sr-only">New in version {version}</span>
                      )}
                    </Pill>
                  ))}
              </div>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

export default memo(ContractProfileEntities);
